import axios from "axios";
import { API_BASE_URL } from "./apiConstants";

const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // Increased to 2 minutes for large files
  maxContentLength: 100 * 1024 * 1024, // 100MB max content length
  maxBodyLength: 100 * 1024 * 1024, // 100MB max body length
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "x-input-storage-manager-type": "AzureStorageManager",
  },
  // Performance optimizations
  decompress: true, // Enable automatic decompression
  transitional: {
    silentJSONParsing: false, // Don't auto-parse large responses as JSON
    forcedJSONParsing: false,
    clarifyTimeoutError: true,
  },
});

// Request Interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    // skipSessionHeaders: the caller sends its own fresh token, and must not
    // carry the stored one or X-User-ID (the copilot host; api-layer.mdc).
    if (!config.skipSessionHeaders) {
      const token = sessionStorage.getItem("accessToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const userId = sessionStorage.getItem("UserID");
      if (userId) {
        config.headers["X-User-ID"] = userId;
      }
    }

    // Optimize for CSV downloads
    if (
      config.url.includes("results_timeseries_csv") ||
      config.url.includes("results_daily_summary_csv")
    ) {
      // Specific optimizations for CSV endpoints
      config.headers["Accept"] = "text/plain, text/csv";
      //config.headers['Accept-Encoding'] = 'gzip, deflate, br'; // Add gzip for CSV endpoints
      config.responseType = "text";
      config.timeout = 180000; // 3 minutes for large CSV files
      config.decompress = true;
      config.transformResponse = [(data) => data]; // Skip JSON parsing

      // Add performance headers
      config.headers["Cache-Control"] = "no-store"; // Don't cache large files
      //   config.headers['Connection'] = 'keep-alive';
    } else {
      // Standard configuration for other endpoints
      config.decompress = true;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    // Log performance metrics for large responses
    if (
      response.data &&
      typeof response.data === "string" &&
      response.data.length > 1024 * 1024
    ) {
      
    }
    return response;
  },
  (error) => {
    if (error.config?.skipSessionHeaders) {
      // The copilot service turns its own errors into notices
      // (features/copilot/domain/copilotErrors.js); these logs would misread them.
      return Promise.reject(error);
    }
    if (error.response) {
      // Server responded with error
      const { status, data } = error.response;

      switch (status) {
        case 401:
          console.error("Session expired. Please login again.");
          break;
        case 403:
          console.error("You do not have permission to perform this action.");
          break;
        case 404:
          console.error("Resource not found.");
          break;
        case 500:
          console.error("Server error. Please try again later.");
          break;
        case 413:
          console.error("Response too large. The file may exceed size limits.");
          break;
        case 408:
        case 504:
          console.error(
            "Request timeout. The file may be too large or connection is slow.",
          );
          break;
        default:
          console.error("Error:", data?.message || "Something went wrong.");
      }
    } else if (error.request) {
      if (error.code === "ECONNABORTED") {
        console.error("Request timeout. Try again or check file size.");
      } else {
        console.error("No response from server. Please check your connection.");
      }
    } else {
      console.error("Error setting up request:", error.message);
    }
    return Promise.reject(error);
  },
);

export default axiosInstance;
